// RBAC Context Provider & Hooks für Fortune 500 Enterprise Security.
//
// As of the 2026-05-22 unification: this file is a thin shim over
// `useActiveTenant()`. The canonical resolver in
// `src/app/api/admin/active-tenant/route.ts` now returns
// `{ tenantId, role, roleId, permissions[], isSuperAdmin, ... }` in
// one server round-trip, so there is no longer any reason for two
// providers to query the database independently.
//
// The shim still exposes the legacy `useRBAC()` / `usePermission()` /
// HOC API so we don't have to migrate the 30+ existing call-sites in
// one go. Internally it just reads from the unified context.
//
// **Why we kept this file**: removing the API surface today would
// require touching every component that calls `useRBAC()`. Instead we
// can migrate call-sites to `useActiveTenant()` opportunistically and
// delete the shim once they're all gone.
'use client';

import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { Role, Permission } from './permissions';

interface RBACContextType {
  userRole: Role | null;
  permissions: Permission[];
  userId: string | null;
  tenantId: string | null;
  loading: boolean;

  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  canAccessRoute: (route: string) => boolean;

  refreshPermissions: () => Promise<void>;
  switchTenant: (tenantId: string) => void;
}

const RBACContext = createContext<RBACContextType | undefined>(undefined);

// Map raw `tenant_user_roles.roles.name` → our Role enum. Anything we
// don't recognise falls back to `guest_user` so the UI stays locked
// down rather than throwing.
const mapDatabaseRoleToRole = (dbRole: string | null | undefined): Role => {
  switch (dbRole) {
    case 'super_admin':
    case 'super_admin_view_as':
      return 'admin';
    case 'admin':
      return 'admin';
    case 'business_owner':
      return 'business_owner';
    case 'hospitality_manager':
      return 'hospitality_manager';
    case 'property_manager':
      return 'property_manager';
    case 'booking_manager':
      return 'booking_manager';
    case 'calendar_manager':
      return 'calendar_manager';
    case 'analytics_viewer':
      return 'analytics_viewer';
    case 'owner':
      return 'business_owner';
    case 'manager':
    case 'staff':
    case 'viewer':
    case 'employee':
    case 'guest_user':
      return 'guest_user';
    default:
      return 'guest_user';
  }
};

// Permission gates required to render specific admin routes. Empty
// list = always allowed (auth-only). Keep this in sync with
// `permissions.ts → NAV_PERMISSIONS` for any new admin route.
const ROUTE_PERMISSIONS: Record<string, Permission[]> = {
  '/admin': [],
  '/admin/bookings': ['booking.view'],
  '/admin/services': ['booking.view'],
  '/admin/calendar-sync': ['calendar_sync.view'],
  '/admin/users': ['users.view'],
  '/admin/permissions': ['users.manage_roles', 'system.admin'],
  '/admin/tenants': ['tenants.view'],
  '/admin/subscription': ['subscription.view'],
  '/admin/analytics': ['analytics.view'],
  '/admin/settings': ['settings.view'],
};

export function RBACProvider({ children }: { children: React.ReactNode }) {
  const active = useActiveTenant();

  // The unified resolver returns string slugs; cast to the enum type
  // expected by the legacy `hasPermission` API. Unknown slugs are
  // harmless — the includes() check just returns false.
  const permissions = active.permissions as Permission[];
  const userRole = mapDatabaseRoleToRole(active.role);

  // Wildcard short-circuit for super_admin / admin so a missing
  // explicit grant doesn't lock platform staff out.
  const hasPermission = useCallback(
    (p: Permission) =>
      !active.loading && (active.isSuperAdmin || permissions.includes(p)),
    [active.loading, active.isSuperAdmin, permissions]
  );
  const hasAnyPermission = useCallback(
    (ps: Permission[]) =>
      !active.loading &&
      (active.isSuperAdmin || ps.some((p) => permissions.includes(p))),
    [active.loading, active.isSuperAdmin, permissions]
  );
  const hasAllPermissions = useCallback(
    (ps: Permission[]) =>
      !active.loading &&
      (active.isSuperAdmin || ps.every((p) => permissions.includes(p))),
    [active.loading, active.isSuperAdmin, permissions]
  );
  const canAccessRoute = useCallback(
    (route: string) => {
      if (active.loading) return false;
      const required = ROUTE_PERMISSIONS[route] ?? [];
      if (required.length === 0) return true;
      return active.isSuperAdmin || required.some((p) => permissions.includes(p));
    },
    [active.loading, active.isSuperAdmin, permissions]
  );

  const value = useMemo<RBACContextType>(
    () => ({
      userRole,
      permissions,
      // Auth user id isn't included in the resolver response (it's a
      // server-side concept). Anything that actually needs it should
      // hit `supabase.auth.getUser()` directly; this null was rarely
      // read in the old codebase anyway.
      userId: null,
      tenantId: active.tenantId,
      loading: active.loading,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      canAccessRoute,
      refreshPermissions: active.refresh,
      switchTenant: (id: string) => {
        void active.switchTo(id);
      },
    }),
    [
      userRole,
      permissions,
      active.tenantId,
      active.loading,
      active.refresh,
      active.switchTo,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      canAccessRoute,
    ]
  );

  return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>;
}

export function useRBAC(): RBACContextType {
  const context = useContext(RBACContext);
  if (context === undefined) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  return context;
}

export function usePermission(permission: Permission): boolean {
  const { hasPermission } = useRBAC();
  return hasPermission(permission);
}

export function useAnyPermission(permissions: Permission[]): boolean {
  const { hasAnyPermission } = useRBAC();
  return hasAnyPermission(permissions);
}

export function useAllPermissions(permissions: Permission[]): boolean {
  const { hasAllPermissions } = useRBAC();
  return hasAllPermissions(permissions);
}

export function useRouteAccess(route: string): boolean {
  const { canAccessRoute } = useRBAC();
  return canAccessRoute(route);
}

export function withPermission<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredPermissions: Permission[],
  fallbackComponent?: React.ComponentType<P> | React.ReactNode
) {
  return function PermissionWrappedComponent(props: P) {
    const { hasAnyPermission, loading } = useRBAC();

    if (loading) {
      return <div>Loading...</div>;
    }

    if (!hasAnyPermission(requiredPermissions)) {
      if (fallbackComponent) {
        if (React.isValidElement(fallbackComponent)) {
          return fallbackComponent as React.ReactElement;
        }
        const FallbackComponent = fallbackComponent as React.ComponentType<P>;
        return <FallbackComponent {...props} />;
      }
      return <div>Access Denied</div>;
    }

    return <WrappedComponent {...props} />;
  };
}
